/**
 * Picks the header logo colour from whatever sits behind it.
 *
 * Only runs while the header is transparent and overlaying an image — every
 * other case is already resolved server-side in header.liquid, so this is a
 * refinement of a good default, never a correction of a broken one.
 *
 * Decision matrix (see plan): a flat surface tolerates the brand colours, a busy
 * photo does not, because a mid-luminance mark disappears into local detail
 * regardless of how it scores against the average.
 *
 *              | flat (low sigma)  | busy (high sigma)
 *   near-white | flatLight         | busyLight
 *   light      | busyLight         | busyLight
 *   dark       | flatDark          | busyDark
 *
 * @element adaptive-logo
 */

const SAMPLE_W = 24;
const SAMPLE_H = 12;
const RENDITION_W = 96;

/** sRGB channel -> linear light. */
const toLinear = (channel) => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

class AdaptiveLogo extends HTMLElement {
  #header;
  #config;
  #observer;
  #stateObserver;
  #frame;
  #abort = new AbortController();

  connectedCallback() {
    this.#header = this.closest('#header-component') || document.querySelector('#header-component');
    if (!this.#header) return;

    // Invert mode is pure CSS (mix-blend-mode), so there is nothing to sample.
    if (this.dataset.mode !== 'sample') return;

    this.#config = {
      flatLight: this.dataset.flatLight,
      flatDark: this.dataset.flatDark,
      busyLight: this.dataset.busyLight,
      busyDark: this.dataset.busyDark,
      // Authored as percentages so they can live in theme settings as ranges.
      flatness: (parseFloat(this.dataset.flatness) || 7) / 100,
      nearWhite: (parseFloat(this.dataset.nearWhite) || 85) / 100,
      adaptNav: this.dataset.adaptNav === 'true',
    };

    const { signal } = this.#abort;
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    window.addEventListener('pageshow', this.#schedule, { signal });

    // The gallery replaces its whole <media-gallery> element on variant change,
    // so watching for subtree churn is more robust than guessing an event name.
    const scope = document.querySelector('product-component');
    if (scope) {
      this.#observer = new MutationObserver(this.#schedule);
      this.#observer.observe(scope, { childList: true, subtree: true });
    }

    // The stuck state is handled in CSS, but clearing the sampled value keeps
    // the custom properties honest for anything else reading them.
    this.#stateObserver = new MutationObserver(this.#schedule);
    this.#stateObserver.observe(this.#header, {
      attributes: true,
      attributeFilter: ['data-sticky-state', 'transparent'],
    });

    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#stateObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#apply().catch(() => this.#reset());
    });
  };

  /** Drop back to the server-rendered colour. */
  #reset() {
    this.#header?.style.removeProperty('--header-logo-color-sampled');
    if (this.#config?.adaptNav) this.#header?.style.removeProperty('--color-transparent-text');
    this.#header?.removeAttribute('data-logo-tone');
  }

  async #apply() {
    // Only meaningful while the header is actually see-through.
    if (!this.#header.hasAttribute('transparent') || this.#header.dataset.stickyState === 'active') {
      this.#reset();
      return;
    }

    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return this.#reset();

    const image = this.#backdropImage(rect);
    if (!image) return this.#reset();

    const stats = await this.#measure(image, rect);
    if (!stats) return this.#reset();

    const { mean, deviation } = stats;
    const { flatLight, flatDark, busyLight, busyDark, flatness, nearWhite } = this.#config;
    const isFlat = deviation < flatness;

    let colour;
    let tone;
    if (isFlat && mean > nearWhite) {
      colour = flatLight;
      tone = 'flat-near-white';
    } else if (mean > 0.5) {
      colour = busyLight;
      tone = isFlat ? 'flat-light' : 'busy-light';
    } else if (isFlat) {
      colour = flatDark;
      tone = 'flat-dark';
    } else {
      colour = busyDark;
      tone = 'busy-dark';
    }

    if (colour) this.#header.style.setProperty('--header-logo-color-sampled', colour);
    this.#header.setAttribute('data-logo-tone', tone);

    // Nav and icons are not exempt from contrast rules, so they only ever go
    // fully dark or fully light — never a brand colour.
    if (this.#config.adaptNav) {
      this.#header.style.setProperty('--color-transparent-text', mean > 0.5 ? busyLight : busyDark);
    }
  }

  /** Topmost image under the logo, ignoring the header's own chrome. */
  #backdropImage(rect) {
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    for (const node of document.elementsFromPoint(x, y)) {
      if (node.closest('#header-group')) continue;
      if (node instanceof HTMLImageElement && node.currentSrc) return node;
      const nested = node.querySelector?.('img');
      if (nested instanceof HTMLImageElement && nested.currentSrc) return nested;
    }
    return null;
  }

  /**
   * Mean luminance and standard deviation of the backdrop under `rect`.
   *
   * Reads a small CDN rendition rather than the displayed element: the on-page
   * image was loaded without CORS and would taint the canvas.
   */
  async #measure(image, rect) {
    const region = this.#regionOf(image, rect);
    if (!region) return null;

    const source = await this.#loadSamplingImage(image.currentSrc);
    if (!source) return null;

    const sx = region.x * source.naturalWidth;
    const sy = region.y * source.naturalHeight;
    const sw = Math.max(1, region.width * source.naturalWidth);
    const sh = Math.max(1, region.height * source.naturalHeight);

    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE_W;
    canvas.height = SAMPLE_H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, SAMPLE_W, SAMPLE_H);

    let data;
    try {
      ({ data } = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H));
    } catch {
      return null; // tainted canvas
    }

    const values = [];
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 8) continue; // ignore transparent pixels
      values.push(0.2126 * toLinear(data[i]) + 0.7152 * toLinear(data[i + 1]) + 0.0722 * toLinear(data[i + 2]));
    }
    if (!values.length) return null;

    const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
    return { mean, deviation: Math.sqrt(variance) };
  }

  /**
   * Where `rect` falls on the image, as fractions of the intrinsic image.
   *
   * Has to undo object-fit: the gallery uses `cover`, so the element shows a
   * crop and mapping viewport coordinates straight onto the file would sample
   * the wrong part of the picture.
   *
   * @returns {{x: number, y: number, width: number, height: number} | null}
   */
  #regionOf(image, rect) {
    const box = image.getBoundingClientRect();
    if (!box.width || !box.height || !image.naturalWidth || !image.naturalHeight) return null;

    const fit = getComputedStyle(image).objectFit;
    const ratioX = box.width / image.naturalWidth;
    const ratioY = box.height / image.naturalHeight;
    const scale =
      fit === 'contain' || fit === 'scale-down'
        ? Math.min(ratioX, ratioY)
        : fit === 'fill'
          ? null
          : Math.max(ratioX, ratioY);

    // `fill` stretches, so the element maps onto the whole image directly.
    const paintedW = scale === null ? box.width : image.naturalWidth * scale;
    const paintedH = scale === null ? box.height : image.naturalHeight * scale;

    // Assumes the default centred object-position, which is what the theme uses.
    const originX = box.left + (box.width - paintedW) / 2;
    const originY = box.top + (box.height - paintedH) / 2;

    const x = clamp((rect.left - originX) / paintedW, 0, 1);
    const y = clamp((rect.top - originY) / paintedH, 0, 1);
    const width = clamp(rect.width / paintedW, 0.001, 1 - x);
    const height = clamp(rect.height / paintedH, 0.001, 1 - y);
    return { x, y, width, height };
  }

  /** Small, CORS-enabled copy of the backdrop so the canvas stays readable. */
  #loadSamplingImage(src) {
    let url = src;
    try {
      const parsed = new URL(src, location.href);
      parsed.searchParams.set('width', String(RENDITION_W));
      parsed.searchParams.delete('height');
      url = parsed.href;
    } catch {
      /* non-parseable src: try it as-is */
    }

    return new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous'; // must precede src or the canvas taints
      probe.onload = () => resolve(probe.naturalWidth ? probe : null);
      probe.onerror = () => resolve(null);
      probe.src = url;
    });
  }
}

if (!customElements.get('adaptive-logo')) {
  customElements.define('adaptive-logo', AdaptiveLogo);
}
